import { Fragment, type ElementType } from 'react';

interface AccentedTitleProps {
  /** The complete heading, e.g. "Signature Facial Services". */
  text: string;
  /** The part rendered in gold, e.g. "Facial". May sit anywhere in the text. */
  accent?: string;
  /** Class applied to the accented span — `gold-text` or `gold-accent`. */
  accentClassName?: string;
  as?: ElementType;
  className?: string;
  id?: string;
}

/**
 * Renders a heading with one highlighted phrase.
 *
 * The original site wraps the gold portion in a span, and that portion is not
 * always the last word — "Signature <span>Facial</span> Services" is real. The
 * accent is therefore matched by position rather than assumed to be a suffix,
 * so the owner can edit the plain heading text in admin without breaking it.
 */
export function AccentedTitle({
  text, accent, accentClassName = 'gold-text', as: Tag = 'h2', className, id,
}: AccentedTitleProps) {
  if (!accent || !text.includes(accent)) {
    return <Tag className={className} id={id}>{text}</Tag>;
  }

  const at = text.lastIndexOf(accent);
  const before = text.slice(0, at);
  const after = text.slice(at + accent.length);

  return (
    <Tag className={className} id={id}>
      {before}
      <span className={accentClassName}>{accent}</span>
      {after}
    </Tag>
  );
}

/**
 * Heading whose text carries its own line breaks (as newlines) and whose
 * accented phrase sits wherever the original put it.
 *
 * The locations heading reads "Two Locations,<br> One <em>Premium</em>
 * Experience" — the accent is mid-sentence and the break is load-bearing, so
 * neither can be reconstructed from a title plus a trailing accent.
 */
export function AccentedLines({
  text, accent, as: Tag = 'h2', accentTag: AccentTag = 'em', className, id,
}: {
  text: string;
  accent?: string;
  as?: ElementType;
  accentTag?: ElementType;
  className?: string;
  id?: string;
}) {
  const lines = text.split('\n');
  // Only the first occurrence is highlighted, matching the single <em> the
  // original markup carries.
  const accentLine = accent ? lines.findIndex((l) => l.includes(accent)) : -1;

  return (
    <Tag className={className} id={id}>
      {lines.map((line, i) => {
        const at = i === accentLine && accent ? line.indexOf(accent) : -1;
        return (
          <Fragment key={`${i}-${line}`}>
            {at >= 0 && accent ? (
              <>
                {line.slice(0, at)}
                <AccentTag>{accent}</AccentTag>
                {line.slice(at + accent.length)}
              </>
            ) : line}
            {i < lines.length - 1 ? <br /> : null}
          </Fragment>
        );
      })}
    </Tag>
  );
}

/**
 * Multi-line heading with an accented final line, matching the hero and section
 * titles that use `<br>` plus an `<em>` on the live site.
 */
export function MultilineTitle({
  lines, accent, as: Tag = 'h1', className, accentTag: AccentTag = 'em', id,
}: {
  lines: string[];
  accent?: string;
  as?: ElementType;
  accentTag?: ElementType;
  className?: string;
  id?: string;
}) {
  return (
    <Tag className={className} id={id}>
      {lines.map((line, i) => (
        <Fragment key={line}>
          {line}
          {i < lines.length - 1 ? <br /> : null}
        </Fragment>
      ))}
      {accent ? <>{lines.length ? ' ' : ''}<AccentTag>{accent}</AccentTag></> : null}
    </Tag>
  );
}
